import { createContext, useContext } from 'react';
import { apiRequest, queryClient } from './queryClient';
import type { PulseTagState, PulseTagUser } from '@shared/pulse-user-tags';
import type { TreeSnapshot } from '@shared/projectTreeDto';
import { updatePulseInSnapshot } from './treeCache';

const EMPTY_USERS: PulseTagUser[] = [];
export const PulseTagRosterContext = createContext<PulseTagUser[]>(EMPTY_USERS);
export const usePulseTagRoster = () => useContext(PulseTagRosterContext);

export function applyPulseTagStates(pulseId: number, states: PulseTagState[], users?: PulseTagUser[], sequenceNumber?: number) {
  queryClient.setQueriesData<{ snapshot: TreeSnapshot; sequenceNumber: number }>({ queryKey: ['/api/tree/snapshot'] }, old => {
    if (!old?.snapshot || (sequenceNumber && old.sequenceNumber > sequenceNumber)) return old;
    const snapshot = updatePulseInSnapshot(old.snapshot, pulseId, { userTags: states });
    if (users) snapshot.years = snapshot.years.map(year => ({ ...year, projects: year.projects.map(project =>
      project.pulses?.some(p => p.id === pulseId) ? { ...project, tagUsers: users } : project
    ) }));
    return { ...old, snapshot };
  });
}

export async function sendPulseTag(pulseId: number, body: {
  userId: number; action: 'tag' | 'urgent' | 'clear'; version?: string; message?: string; requestId: string;
}) {
  try {
    const response = await apiRequest('POST', `/api/pulses/${pulseId}/user-tags`, body);
    const result = await response.json();
    applyPulseTagStates(pulseId, result.states, result.users, result.sequenceNumber);
  } catch (error) {
    // A lost response/409 must reconcile the committed state, not leave a stale clickable chip.
    void queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
    throw error;
  }
}

const readInFlight = new Set<number>();
/** Explicit opening only; dedupe overlapping workbench/row/modal events. */
export async function recordPulseRead(pulseId: number) {
  if (readInFlight.has(pulseId)) return;
  readInFlight.add(pulseId);
  try {
    const response = await apiRequest('POST', `/api/pulses/${pulseId}/user-tags/read`, {});
    const result = await response.json();
    if (result.states) applyPulseTagStates(pulseId, result.states, undefined, result.sequenceNumber);
  } catch (error) {
    console.error('[PulseTags] Could not record pulse reading', error);
  } finally { readInFlight.delete(pulseId); }
}
